import React, {
  forwardRef,
  PropsWithChildren,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import * as S from './Select.styles';
import { AngleBackIcon } from '@/components/UI/Icons/Icons';
import { RequiredStar } from '@/components/UI/RequiredStar/RequiredStar';
import { HelperText } from '@/components/UI/HelperText/HelperText';
import { SelectProps, SelectRef } from '@/components/UI/Select/Select.types';
import { InputClear } from '@/components/UI/Input/Input.styles';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';

export const Select = forwardRef(function Select(
  {
    children,
    id,
    name,
    label,
    iconSrc,
    icon,
    hasIcon,
    toggle,
    value,
    hasBg,
    hasBorderBottom,
    isRequired,
    isOpen,
    hasSearch,
    onSearch,
    helperText,
    hasError,
    visibleValue,
    onClear,
    appearance = 'default',
    hasDropdown,
  }: PropsWithChildren<SelectProps>,
  ref: React.Ref<SelectRef>
) {
  const theme = useTheme();
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState('');

  const selectValue = visibleValue ?? value;
  const isSearchInputVisible = !!hasSearch && isOpen;
  const isClearVisible =
    (selectValue && onClear) || (searchValue && isSearchInputVisible);

  function clear(e?: React.MouseEvent<HTMLButtonElement>) {
    e?.stopPropagation();

    if (searchValue && isSearchInputVisible) {
      clearSearchValue();
      return;
    }
    onClear?.();
  }

  function clearSearchValue() {
    handleChangeSearchValue('');
  }

  useImperativeHandle(ref, () => ({ clearSearchValue }), []);

  useEffect(() => {
    if (!isOpen || !hasDropdown) return;

    function handleClickOutside(event: MouseEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        clearSearchValue();
        toggle(false);
      }
    }

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, hasDropdown]);

  useEffect(() => {
    if (isSearchInputVisible && inputRef.current) {
      inputRef.current?.focus();
    }
  }, [isSearchInputVisible, inputRef.current]);

  function getSelectImg() {
    if (icon) {
      return <S.SelectImgBox>{icon}</S.SelectImgBox>;
    }

    if (iconSrc) {
      return (
        <S.SelectImgWithScrBox>
          <img
            src={iconSrc}
            alt={label}
            width={35}
            height={35}
            loading="lazy"
          />
        </S.SelectImgWithScrBox>
      );
    }

    return (
      <IconBox
        $filename="categories.svg"
        $width={40}
        $height={40}
        $iconWidth={17}
        $iconHeight={17}
      />
    );
  }

  function handleChangeSearchValue(
    e: React.ChangeEvent<HTMLInputElement> | string
  ) {
    const newValue = typeof e === 'string' ? e : e.target.value;
    setSearchValue(newValue);
    onSearch?.(newValue);
  }

  const isLabelSmall = !!value || isSearchInputVisible;

  return (
    <S.SelectWrapper ref={wrapperRef}>
      <S.SelectBox
        id={id}
        name={name}
        onClick={() => toggle()}
        $hasBg={hasBg}
        $appearance={appearance}
        $isOpen={isOpen}
        $hasBorderBottom={hasBorderBottom}
        $hasError={hasError}
        data-testid="select"
      >
        <S.SelectValue data-testid="select-value">
          {hasIcon && getSelectImg()}
          <S.SelectTexts>
            <S.SelectLabel
              data-testid={isLabelSmall ? 'select-label-small' : 'select-label'}
              $variant="3"
              $top={isLabelSmall ? '8px' : 'calc(50% - 8.25px)'}
              $hasError={hasError}
            >
              {label} {isRequired && <RequiredStar />}
            </S.SelectLabel>

            {isSearchInputVisible && (
              <S.SelectSearchInput
                ref={inputRef}
                onClick={e => e.stopPropagation()}
                name="searchValue"
                value={searchValue}
                placeholder={t('common.search')}
                onChange={handleChangeSearchValue}
              />
            )}

            {!!value && (
              <S.SelectText
                data-testid="select-text"
                $isHidden={isSearchInputVisible}
              >
                {selectValue}
              </S.SelectText>
            )}
          </S.SelectTexts>
        </S.SelectValue>

        {isClearVisible ? (
          <InputClear
            $isYCentered
            onClick={clear}
            onPointerDown={e => e.preventDefault()}
            data-testid="select-clear"
            {...(appearance === 'filled' && {
              $customPaddingRight: 15,
            })}
          />
        ) : (
          <S.SelectAngle $appearance={appearance} data-testid="select-angle">
            <AngleBackIcon
              {...(appearance === 'filled' && {
                fill: isOpen
                  ? theme.palette.brand.main
                  : theme.palette.primary.labels,
                rotateAngle: isOpen ? -90 : 90,
              })}
            />
          </S.SelectAngle>
        )}
      </S.SelectBox>
      {helperText && (
        <HelperText
          helperText={helperText}
          hasError={hasError}
          hasBottomMargin={false}
        />
      )}
      {children}
    </S.SelectWrapper>
  );
});
